import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';

const PromoCodeForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoader, setIsLoader] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        title_ar: '',
        code: '',
        min_order: '',
        max_order: '',
        discount_type: 'percentage',
        discount: '',
        expiry_type: 'date',
        expiry_date: '',
        expiry_count: '',
        user_type: 'all',
    });

    useEffect(() => {
        if (id) getSingleData();
    }, [id]);

    const getSingleData = async () => {
        setIsLoader(true);
        try {
            const response = await ApiService.request({ method: 'GET', url: `admin/promo-codes/${id}` });
            const data = response.data;
            if (data.status) {
                const d = data.data;
                setFormData({
                    title: d.title || '',
                    title_ar: d.title_ar || '',
                    code: d.code || '',
                    min_order: d.min_order || '',
                    max_order: d.max_order || '',
                    discount_type: d.discount_type || 'percentage',
                    discount: d.discount || '',
                    expiry_type: d.expiry_type || 'date',
                    expiry_date: d.expiry_date ? d.expiry_date.split('T')[0] : '',
                    expiry_count: d.expiry_count || '',
                    user_type: d.user_type || 'all',
                });
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoader(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/promo-codes/createOrUpdate/${id || ''}`,
                data: formData,
            });
            if (response.data.status) {
                toast.success(response.data.message);
                navigate('/promo-codes');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Form submission failed:', error);
        } finally {
            setIsLoader(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {id ? 'Edit' : 'Add New'} Promo Code
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/promo-codes" className="text-muted text-hover-primary">Promo Codes</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-body pt-6">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-9 mb-6">
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">Title</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            placeholder="Enter title"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">Title Arabic</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            placeholder="Enter title in Arabic"
                                            name="title_ar"
                                            value={formData.title_ar}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">Promo Code</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            placeholder="e.g. SAVE20"
                                            name="code"
                                            value={formData.code}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">Discount Type</label>
                                        <select
                                            className="form-control form-control-solid"
                                            name="discount_type"
                                            value={formData.discount_type}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="percentage">Percentage (%)</option>
                                            <option value="fixed">Fixed Amount</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">
                                            Discount {formData.discount_type === 'percentage' ? '(%)' : '(Amount)'}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control form-control-solid"
                                            placeholder={formData.discount_type === 'percentage' ? 'e.g. 20' : 'e.g. 50'}
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            min="0"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="fs-6 fw-semibold mb-2">Min Order Amount</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-solid"
                                            placeholder="e.g. 100"
                                            name="min_order"
                                            value={formData.min_order}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="fs-6 fw-semibold mb-2">Max Order Amount</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-solid"
                                            placeholder="e.g. 1000 (optional)"
                                            name="max_order"
                                            value={formData.max_order}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">User Type</label>
                                        <select
                                            className="form-control form-control-solid"
                                            name="user_type"
                                            value={formData.user_type}
                                            onChange={handleChange}
                                        >
                                            <option value="all">All Users</option>
                                            <option value="new">New Users</option>
                                            <option value="existing">Existing Users</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">Expiry Type</label>
                                        <select
                                            className="form-control form-control-solid"
                                            name="expiry_type"
                                            value={formData.expiry_type}
                                            onChange={handleChange}
                                        >
                                            <option value="date">By Date</option>
                                            <option value="count">By Usage Count</option>
                                        </select>
                                    </div>
                                    {formData.expiry_type === 'date' ? (
                                        <div className="col-md-6 fv-row">
                                            <label className="required fs-6 fw-semibold mb-2">Expiry Date</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-solid"
                                                name="expiry_date"
                                                value={formData.expiry_date}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    ) : (
                                        <div className="col-md-6 fv-row">
                                            <label className="required fs-6 fw-semibold mb-2">Max Usage Count</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-solid"
                                                placeholder="e.g. 100"
                                                name="expiry_count"
                                                value={formData.expiry_count}
                                                onChange={handleChange}
                                                min="1"
                                                required
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row g-9 my-3">
                                    <div className="text-center">
                                        <Link to="/promo-codes" className="btn btn-light me-3">Back</Link>
                                        <button type="submit" className="btn btn-base" disabled={isLoader}>
                                            <span className="indicator-label">
                                                {isLoader ? <Loader size="20" color="white" text="Submitting" /> : 'Submit'}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromoCodeForm;
